import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { add, formatISO } from 'date-fns'
import { datadogRum } from '@datadog/browser-rum'
import { ClientError } from 'graphql-request'
import { SITE_CREDITS } from 'orgs-sites/site'
import * as api from 'src/services'
import { useSite } from 'src/contexts/site'
import {
  GET_CREDIT_INFO_QUERY_KEY,
  MODELS_QUERY_KEY,
  MODEL_INPUT_TAGS_QUERY_KEY,
  MODEL_QUERY_KEY,
} from 'models/api'
import { addSubscription } from 'src/utility/subscriptionHelper'
import * as events from 'src/utility/events'

export function useArchiveModelMutation(): UseMutationResult<
  api.GqlModelIdStateFragment | null | undefined,
  unknown,
  api.FetchModel
> {
  const queryClient = useQueryClient()
  const { id: currentFactory } = useSite()

  return useMutation({
    mutationFn: async (model: api.FetchModel) =>
      api.archiveModel({ id: model.id }),
    onSuccess: (_, model) => {
      events.modelArchived({
        id: model.id,
        type: model.__typename,
      })
      queryClient.invalidateQueries([MODELS_QUERY_KEY, currentFactory])
      queryClient.invalidateQueries([MODEL_QUERY_KEY, model.id])
    },
  })
}

export function useAddInputTagMutation(): UseMutationResult<
  api.GqlModelBaseFragment | null | undefined,
  unknown,
  api.InputTag
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: api.InputTag) => api.addInputTag(input),
    onSuccess: (_, input) => {
      queryClient.invalidateQueries([MODEL_QUERY_KEY, input.modelId])
      queryClient.invalidateQueries([MODEL_INPUT_TAGS_QUERY_KEY, input.modelId])
    },
  })
}

export function useRemoveInputTagMutation(): UseMutationResult<
  api.GqlModelBaseFragment | null | undefined,
  unknown,
  api.InputTag
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: api.InputTag) => api.removeInputTag(input),
    onSuccess: (data, input) => {
      queryClient.invalidateQueries([MODEL_QUERY_KEY, input.modelId])
      queryClient.invalidateQueries([MODEL_INPUT_TAGS_QUERY_KEY, input.modelId])
    },
  })
}

export function useActivateModelMutation(): UseMutationResult<
  api.GqlModelIdStateFragment | undefined | null,
  Error,
  api.FetchModel
> {
  const { id: currentFactory } = useSite()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (model: api.FetchModel) => {
      const input = { id: model.id }
      datadogRum.addAction('Model activated', input)
      return api.activateModel(input)
    },
    onSuccess: (_, model) => {
      events.modelActivated({
        id: model.id,
        type: model.__typename,
      })
      queryClient.invalidateQueries([MODEL_QUERY_KEY, model.id])
      queryClient.invalidateQueries([MODELS_QUERY_KEY, currentFactory])
      // Invalidate credits query
      queryClient.invalidateQueries([GET_CREDIT_INFO_QUERY_KEY, currentFactory])
      queryClient.invalidateQueries([SITE_CREDITS, currentFactory])
    },
    onError: error => {
      if (
        error instanceof ClientError &&
        error.response?.errors?.[0]?.extensions?.code === 'SUBSCRIPTION_LIMIT'
      ) {
        // Invalidate credits query to force a refetch
        // and show the banner with error message
        queryClient.invalidateQueries([
          GET_CREDIT_INFO_QUERY_KEY,
          currentFactory,
        ])
        queryClient.invalidateQueries([SITE_CREDITS, currentFactory])
      }
    },
  })
}

export function useDeactivateModelMutation(): UseMutationResult<
  api.GqlModelIdStateFragment | undefined | null,
  unknown,
  api.FetchModel
> {
  const { id: currentFactory } = useSite()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (model: api.FetchModel) => {
      const input = { id: model.id }
      datadogRum.addAction('Model deactivated', input)
      return api.deactivateModel(input)
    },
    onSuccess: (_, model) => {
      events.modelDeactivated({
        id: model.id,
        type: model.__typename,
      })
      queryClient.invalidateQueries([MODEL_QUERY_KEY, model.id])
      queryClient.invalidateQueries([MODELS_QUERY_KEY, currentFactory])
      // Invalidate credits query
      queryClient.invalidateQueries([GET_CREDIT_INFO_QUERY_KEY, currentFactory])
      queryClient.invalidateQueries([SITE_CREDITS, currentFactory])
    },
  })
}

export function useStartModelTrainingMutation(
  model: api.FetchModel,
): UseMutationResult<
  api.GqlModelIdStateFragment | undefined | null,
  unknown,
  api.GqlActivateModelInput
> {
  const { id: currentFactory } = useSite()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: api.GqlActivateModelInput) => {
      datadogRum.addAction('Model training started', input)
      return api.startModelTraining(input)
    },
    onSuccess: (_, input) => {
      // add subscription to poll for model state
      const expire = formatISO(add(new Date(), { hours: 24 }))
      const load = {
        topic: 'trainModel',
        entity: input.id,
        event:
          model.__typename === 'ForecastModel'
            ? 'NOT_RUNNING'
            : 'AWAITING_ANOMALY_ANALYSIS',
        expire,
      }
      addSubscription(load)
      events.modelTrained({
        id: input.id,
        type: model.__typename,
        configurationName: input.configurationName,
      })
      queryClient.invalidateQueries([MODEL_QUERY_KEY, input.id])
      queryClient.invalidateQueries([MODELS_QUERY_KEY, currentFactory])
    },
  })
}

export function useRunAnomalyDetectionMutation(): UseMutationResult<
  api.GqlModelIdStateFragment | undefined | null,
  unknown,
  api.GqlRunAnomalyDetectionInput
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: api.GqlRunAnomalyDetectionInput) =>
      api.runAnomalyDetection(input),
    onSuccess: (_, variables) => {
      const expire = formatISO(add(new Date(), { hours: 24 }))
      const load = {
        topic: 'anomalyDetection',
        entity: variables.id,
        event: 'NOT_RUNNING',
        expire,
      }
      addSubscription(load)
      queryClient.invalidateQueries([MODEL_QUERY_KEY, variables.id])
    },
  })
}

export function useAddStarMutation(
  currentFactory: string,
): UseMutationResult<
  api.GqlStarrableStateFragment | null | undefined,
  unknown,
  api.GqlAddStarInput,
  unknown
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: api.GqlAddStarInput) => api.addStar(input),
    onMutate: async input => {
      // Optimistically update the query data on models page
      queryClient.setQueryData(
        [MODELS_QUERY_KEY, currentFactory],
        (data: api.GqlModelBaseFragment[] | undefined) => {
          return data?.map(m => {
            if (m.id === input.starrableId) {
              return { ...m, viewerHasStarred: true }
            }
            return m
          })
        },
      )
    },
    onSuccess: (_, input) => {
      queryClient.invalidateQueries([MODEL_QUERY_KEY, input.starrableId])
      queryClient.invalidateQueries([MODELS_QUERY_KEY])
    },
  })
}

export function useRemoveStarMutation(
  currentFactory: string,
): UseMutationResult<
  api.GqlStarrableStateFragment | null | undefined,
  unknown,
  api.GqlRemoveStarInput,
  unknown
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: api.GqlRemoveStarInput) => api.removeStar(input),
    onMutate: async input => {
      // Optimistically update the query data on models page
      queryClient.setQueryData(
        [MODELS_QUERY_KEY, currentFactory],
        (data: api.GqlModelBaseFragment[] | undefined) => {
          return data?.map(m => {
            if (m.id === input.starrableId) {
              return { ...m, viewerHasStarred: false }
            }
            return m
          })
        },
      )
    },
    onSuccess: (_, input) => {
      queryClient.invalidateQueries([MODEL_QUERY_KEY, input.starrableId])
      queryClient.invalidateQueries([MODELS_QUERY_KEY])
    },
  })
}
